import React, { useEffect, Suspense, lazy, createElement } from 'react';
import { SocialProof } from './components/SocialProof';
import { PainPoints } from './components/PainPoints';
import { ProductShowcase } from './components/ProductShowcase';
const WhatYouGet = lazy(() => import('./components/WhatYouGet').then(m => ({
  default: m.WhatYouGet
})));
const Bonuses = lazy(() => import('./components/Bonuses').then(m => ({
  default: m.Bonuses
})));
const Countdown = lazy(() => import('./components/Countdown').then(m => ({
  default: m.Countdown
})));
const Pricing = lazy(() => import('./components/Pricing').then(m => ({
  default: m.Pricing
})));
const ProBusiness = lazy(() => import('./components/ProBusiness').then(m => ({
  default: m.ProBusiness
})));
const Guarantee = lazy(() => import('./components/Guarantee').then(m => ({
  default: m.Guarantee
})));
const Testimonials = lazy(() => import('./components/Testimonials').then(m => ({
  default: m.Testimonials
})));
const FAQ = lazy(() => import('./components/FAQ').then(m => ({
  default: m.FAQ
})));
const DarkCTA = lazy(() => import('./components/DarkCTA').then(m => ({
  default: m.DarkCTA
})));
const FinalCTA = lazy(() => import('./components/FinalCTA').then(m => ({
  default: m.FinalCTA
})));
const Footer = lazy(() => import('./components/Footer').then(m => ({
  default: m.Footer
})));
const PurchaseNotifications = lazy(() => import('./components/PurchaseNotifications').then(m => ({
  default: m.PurchaseNotifications
})));
export function App() {
  useEffect(() => {
    // Page title
    document.title = 'Megapack Domina Tu Mente — Construye Progreso™';
    // Lang
    document.documentElement.lang = 'es';
    const setMeta = (name: string, content: string, property?: boolean) => {
      const attr = property ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.content = content;
    };
    const description = 'Sistema completo para resetear tu dopamina y recuperar el control de tu atención. 6 Tomos + 4 Bonos. De Coma Digital a Ejecución Total. Acceso inmediato.';
    const url = 'https://dominatumente.construyeprogreso.com';
    const image = 'https://cdn.magicpatterns.com/uploads/v7HbDBttB6KwqMN8KX8fXz/hf_20260408_171512_ee149707-ab8e-4b03-a52c-19e854e7886e_(1)-Photoroom.webp';
    setMeta('description', description);
    setMeta('keywords', 'domina tu mente, megapack, foco, productividad, dopamina, procrastinación, sistema de ejecución, identidad ejecutora, construye progreso, Argentina');
    setMeta('robots', 'index, follow');
    setMeta('author', 'Construye Progreso™');
    setMeta('viewport', 'width=device-width, initial-scale=1');
    if (!document.querySelector('link[rel="canonical"]')) {
      const canonical = document.createElement('link');
      canonical.rel = 'canonical';
      canonical.href = url;
      document.head.appendChild(canonical);
    }
    // Open Graph
    setMeta('og:title', 'Megapack Domina Tu Mente — De Coma Digital a Ejecución Total', true);
    setMeta('og:description', description, true);
    setMeta('og:image', image, true);
    setMeta('og:url', url, true);
    setMeta('og:type', 'product', true);
    setMeta('og:locale', 'es_AR', true);
    setMeta('og:site_name', 'Construye Progreso™', true);
    // Twitter Card
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', 'Megapack Domina Tu Mente — Construye Progreso™');
    setMeta('twitter:description', description);
    setMeta('twitter:image', image);
    // Product JSON-LD
    if (!document.querySelector('script[data-ld="product"]')) {
      const ld = document.createElement('script');
      ld.type = 'application/ld+json';
      ld.setAttribute('data-ld', 'product');
      ld.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: 'Megapack Domina Tu Mente — De Coma Digital a Ejecución Total',
        description: description,
        image: image,
        brand: {
          '@type': 'Brand',
          name: 'Construye Progreso™'
        },
        offers: {
          '@type': 'AggregateOffer',
          lowPrice: '19990',
          highPrice: '27990',
          priceCurrency: 'ARS',
          availability: 'https://schema.org/InStock',
          offerCount: 2
        },
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: '4.9',
          reviewCount: '1243'
        }
      });
      document.head.appendChild(ld);
    }
    // Organization JSON-LD
    if (!document.querySelector('script[data-ld="org"]')) {
      const orgLd = document.createElement('script');
      orgLd.type = 'application/ld+json';
      orgLd.setAttribute('data-ld', 'org');
      orgLd.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'Construye Progreso™',
        url: url,
        logo: image,
        email: 'construyeprogresoo@gmail.com',
        sameAs: ['https://www.instagram.com/construyeprogresoo']
      });
      document.head.appendChild(orgLd);
    }
    // FAQ JSON-LD
    if (!document.querySelector('script[data-ld="faq"]')) {
      const faqLd = document.createElement('script');
      faqLd.type = 'application/ld+json';
      faqLd.setAttribute('data-ld', 'faq');
      faqLd.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [{
          '@type': 'Question',
          name: '¿Qué incluye el Megapack Domina Tu Mente?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: '6 Tomos completos (de Coma Digital a Ejecución Total), 4 Bonos exclusivos, protocolos de reset dopamínico, sistema de 3 bloques y la metodología de Identidad Ejecutora.'
          }
        }, {
          '@type': 'Question',
          name: '¿Cómo recibo el material?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Todo es 100% digital en formato PDF. Recibís acceso inmediato en tu email dentro de los 5 minutos posteriores a la compra.'
          }
        }, {
          '@type': 'Question',
          name: '¿Tiene garantía?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Sí, ofrecemos garantía de 30 días sin preguntas. Si no te sirve, te devolvemos el 100% de tu dinero.'
          }
        }, {
          '@type': 'Question',
          name: '¿Necesito fuerza de voluntad para que funcione?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'No. El sistema está diseñado para que el foco sea el camino de menor resistencia. No necesitás motivación ni disciplina extrema para empezar.'
          }
        }]
      });
      document.head.appendChild(faqLd);
    }
  }, []);
  // Facebook Pixel
  useEffect(() => {
    if (typeof window !== 'undefined' && !(window as any).fbq) {
      const f = window as any;
      const n = f.fbq = function () {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = true;
      n.version = '2.0';
      n.queue = [];
      (window as any).fbq('init', '1215304437119773');
      (window as any).fbq('track', 'PageView');
      const t = document.createElement('script');
      t.async = true;
      t.src = 'https://connect.facebook.net/en_US/fbevents.js';
      const s = document.getElementsByTagName('script')[0];
      s?.parentNode?.insertBefore(t, s);
    }
  }, []);
  return <>
      {/* Meta Pixel noscript fallback */}
      <noscript data-id="element-0">
        <img height="1" width="1" style={{
        display: 'none'
      }} src="https://www.facebook.com/tr?id=1215304437119773&ev=PageView&noscript=1" alt="" data-id="element-1" />
      </noscript>

      {/* HERO */}
      <header className="relative flex w-full flex-col items-center justify-center text-center text-gray-900 text-base leading-6 px-4 sm:px-5 py-8 sm:py-12" role="banner" style={{
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      backgroundImage: 'linear-gradient(135deg, #0a0a0a 0%, #111111 40%, #1a1a1a 100%)',
      backgroundColor: '#0a0a0a'
    }} data-id="element-2">
        {/* Logo */}
        <div className="mb-2" data-id="element-3">
          <span className="text-xl sm:text-2xl font-black tracking-[2.4px] text-yellow-400" data-id="element-4">
            🧠 CONSTRUYE PROGRESO™
          </span>
        </div>

        {/* Headline */}
        <h1 className="sm:text-5xl md:text-[72px] sm:leading-tight md:leading-[80px] font-black max-w-4xl m-0 text-[35px] leading-[44px] text-white" data-id="element-5">
          De Coma Digital
          <br data-id="element-6" />
          a Ejecución
          <br data-id="element-7" />
          <span className="text-yellow-400" data-id="element-8">Total.</span>
        </h1>

        {/* Product Image */}
        <div className="my-6 w-full max-w-[360px] sm:max-w-[400px] aspect-square relative group" style={{
        perspective: '800px'
      }} data-id="element-9">
          <div className="absolute inset-4 rounded-full blur-3xl opacity-40 -z-10" style={{
          backgroundColor: 'rgb(234, 179, 8)'
        }} data-id="element-10" />
          <img src="https://cdn.magicpatterns.com/uploads/jZcnfSg1t4chYc7hwC2Xy9/hf_20260410_200107_c6d7f656-a3f9-4261-89b5-b628500d8ad2-Photoroom.webp" alt="Megapack Domina Tu Mente — Sistema Completo De Coma Digital a Ejecución Total" width="400" height="400" fetchPriority="high" decoding="sync" className="block max-w-full w-full h-auto object-contain relative z-10 drop-shadow-2xl transition-transform duration-500 ease-out group-hover:scale-105" style={{
          transform: 'rotateY(-4deg) rotateX(2deg)',
          transformStyle: 'preserve-3d'
        }} data-id="element-11" />
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-3/4 h-6 rounded-full blur-xl opacity-20" style={{
          backgroundColor: 'rgb(0, 0, 0)'
        }} data-id="element-12" />
        </div>

        {/* Tagline */}
        <p className="max-w-3xl text-lg sm:text-xl font-bold text-yellow-400 m-0 mb-2" data-id="element-13">
          "Dejá de intentar. Empezá a TERMINAR."
        </p>

        {/* Description */}
        <p className="max-w-2xl py-2 text-base sm:text-lg text-gray-300 leading-7 mb-2 mt-0" data-id="element-14">
          Un sistema completo de 6 Tomos que resetea tu dopamina y recupera el
          control de tu atención. Protocolos biológicos reales, sin fuerza de
          voluntad requerida.{' '}
          <span className="font-bold text-white" data-id="element-15">
            Recuperá 4 horas de foco profundo desde el día uno.
          </span>
        </p>

        {/* Tags */}
        <div className="mb-4 flex max-w-none flex-wrap items-center justify-center gap-2" data-id="element-16">
          {['📄 100% PDF', '⚡ Acceso Inmediato', '🇦🇷 Argentina', '🧠 Reset Dopamínico', '✅ Sin Fuerza de Voluntad', '🛡️ Garantía 30 Días'].map(tag => <span key={tag} className="whitespace-nowrap bg-white/10 text-xs sm:text-sm font-semibold text-gray-200 leading-5 border border-yellow-400/30 rounded-full px-3 py-1" data-id="element-17">
              {tag}
            </span>)}
        </div>

        {/* CTA Button */}
        <a href="#pricing" onClick={() => {
        if (typeof window !== 'undefined' && (window as any).fbq) {
          ;
          (window as any).fbq('track', 'Lead', {
            content_name: 'Hero CTA Click',
            content_category: 'Domina Tu Mente'
          });
        }
      }} className="block w-full max-w-sm bg-yellow-400 text-gray-900 text-lg sm:text-xl font-black rounded-xl px-6 sm:px-8 py-4 mb-4 no-underline transition-colors animate-pulse-green will-change-transform text-center" data-id="element-18">
          🧠 → QUIERO DOMINAR MI MENTE AHORA
        </a>

        {/* Trust Badges */}
        <p className="flex flex-col sm:flex-row flex-wrap items-center justify-center gap-2 sm:gap-3 text-xs sm:text-sm text-gray-400 m-0" data-id="element-19">
          <span data-id="element-20">🛡️ Garantía 30 días sin preguntas</span>
          <span data-id="element-21">🔒 Pago 100% seguro</span>
          <span data-id="element-22">💳 Mercado Pago | Tarjeta | Transferencia</span>
        </p>
      </header>

      {/* MAIN */}
      <main data-id="element-23">
        <SocialProof data-id="element-24" />
        <PainPoints data-id="element-25" />
        <ProductShowcase data-id="element-26" />
        <Suspense fallback={null} data-id="element-27">
          <WhatYouGet data-id="element-28" />
          <Bonuses data-id="element-29" />
          <Countdown data-id="element-30" />
          <Pricing data-id="element-31" />
          <ProBusiness data-id="element-32" />
          <Guarantee data-id="element-33" />
          <Testimonials data-id="element-34" />
          <FAQ data-id="element-35" />
          <DarkCTA data-id="element-36" />
          <FinalCTA data-id="element-37" />
        </Suspense>
      </main>

      <Suspense fallback={null} data-id="element-38">
        <Footer data-id="element-39" />
        <PurchaseNotifications data-id="element-40" />
      </Suspense>
    </>;
}